import { apiUtils } from "../../../utils/apiUtils";
import type {
  RecentActivityItem,
  RecentActivityListResponse,
  RecentActivityPaging,
} from "../types/recentActivity.types";

export interface RecentActivityRequestOptions {
  signal?: AbortSignal;
}

interface RawRecentActivityItem extends Omit<RecentActivityItem, "activity_type"> {
  activity_type?: string;
  activityType?: string;
}

interface RecentActivityListEnvelope {
  paging?: Partial<RecentActivityPaging> | null;
  Paging?: Partial<RecentActivityPaging> | null;
  list?: RawRecentActivityItem[] | null;
  List?: RawRecentActivityItem[] | null;
}

const toRecentActivityItem = (activity: RawRecentActivityItem): RecentActivityItem => ({
  id: activity.id,
  title: activity.title,
  ...(activity.activity_type ?? activity.activityType
    ? { activity_type: activity.activity_type ?? activity.activityType }
    : {}),
  ...(activity.organization_id ? { organization_id: activity.organization_id } : {}),
  created_at: activity.created_at,
  updated_at: activity.updated_at,
});

const normalizePaging = (envelope: RecentActivityListEnvelope): RecentActivityPaging => {
  const paging = envelope.paging ?? envelope.Paging;
  const count = paging?.count ?? 0;

  return {
    count,
    index: paging?.index ?? 0,
    size: paging?.size ?? 0,
    total: paging?.total ?? count,
  };
};

const hasActivityList = (value: unknown): value is RecentActivityListEnvelope => {
  if (typeof value !== "object" || value === null) return false;
  const envelope = value as RecentActivityListEnvelope;
  return Array.isArray(envelope.list) || Array.isArray(envelope.List);
};

export async function getRecentActivityList(
  index: number,
  size: number,
  options?: RecentActivityRequestOptions,
): Promise<RecentActivityListResponse> {
  const data = await apiUtils.getData<RecentActivityListEnvelope>(
    "/v1/portal/dashboard/recent-activity",
    "Failed to fetch recent activity list",
    { params: { index, size }, signal: options?.signal },
  );

  if (!hasActivityList(data)) {
    throw new Error("Invalid recent activity response structure");
  }

  return {
    paging: normalizePaging(data),
    list: (data.list ?? data.List ?? []).map(toRecentActivityItem),
  };
}
